import { FC, useEffect, useState } from 'react';
import { useAchievementQuery } from '../../graphql/generated/operations/achievement.ts';
import { LoadingBackdrop } from '../../components/loading.backdrop.tsx';
import { addDays, addHours, format, startOfWeek } from 'date-fns';
import { Card, CardContent, Typography, Grid, Stack, Chip } from '@mui/material';
import { CompletionStatus } from '../../graphql/generated/graphql.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceFrown, faFaceSmile, faStar as outlineStar } from '@fortawesome/free-regular-svg-icons';
import { faArrowDown, faArrowUp, faStar } from '@fortawesome/free-solid-svg-icons';

export interface Weeks {
  weekOf: string;
  positiveMood: number;
  negativeMood: number;
  moodCount: number;
  activityCount: number;
  motivation: number;
  motivationCount: number;
  weekNumber: number;
}

const getMessage = (md: boolean, ac: boolean, mo: boolean) => {
  if (md && ac && mo) {
    return 'Way to go! Your mood is improving.  This may be because you are adding challenging and meaningful activities to your life.  If you are thinking about quitting smoking, check out the resources page to explore your options';
  }
  if (!md && !ac && !mo) {
    return 'It’s ok that you didn’t do your activities this week.  But it looks like not taking time for yourself may have impacted your mood.  Tip:  carve out some time for you this week to do your activity. Not into it?  Try changing your selected activity to something else.';
  }
  if (!md && ac && mo) {
    return 'It’s great that your mood is improving!  Try one or both of your selected activities this week to further boost your mood.';
  }
  if (md && ac && !mo) {
    return 'Great! You are doing your activities and your mood is improving.  You are laying the foundation for a successful quit smoking attempt, when you are ready to quit.';
  }
  if (md && !ac && mo) {
    return 'Your mood has been improving and you are becoming more motivated to quit.  Try some of your selected activities—or select new ones—to further help boost your mood and motivation to quit!  Feeling good about yourself by accomplishing your activities will make a quit attempt more successful.  Start exploring your nicotine replacement and medications to help you quit?';
  }
  if (!md && ac && !mo) {
    return 'Kudos to you for doing your activities!  Keep up the good work and soon, you’ll notice improvements in your mood.';
  }
  if (!md && !ac && mo) {
    return 'It looks like you are becoming more motivated to quit smoking.  Consider exploring your options for treatments that are proven effective, such as nicotine replacement and Chantix and Zyban.';
  }
  if (md && !ac && !mo) {
    return 'Your mood is improving!  Try planning some activities this week to keep the momentum going and further boost your mood.';
  }
  return '';
};

export const AchievementPage: FC = () => {
  const { data, loading } = useAchievementQuery();
  const [history, setHistory] = useState<Weeks[]>([]);
  const offset = new Date().getTimezoneOffset() / 60;

  useEffect(() => {
    if (data) {
      const newHistory: Record<string, Weeks> = {};
      data.historicalActivities.forEach((a) => {
        const weekOf = format(addHours(new Date(a.weekOf), offset), 'yyyy-MM-dd');
        if (!newHistory[weekOf]) {
          newHistory[weekOf] = {
            activityCount: 0,
            negativeMood: 0,
            positiveMood: 0,
            moodCount: 0,
            weekOf,
            weekNumber: 0,
            motivation: 0,
            motivationCount: 0
          };
        }
        newHistory[weekOf].activityCount += a.schedule.reduce(
          (count, s) => count + (s.completed?.filter((c) => c === CompletionStatus.Completed || c === CompletionStatus.PartiallyCompleted).length ?? 0),
          0
        );
      });
      data.positiveMood.forEach((d) => {
        const weekOf = format(startOfWeek(addHours(d.answeredAt, offset), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!newHistory[weekOf]) {
          newHistory[weekOf] = {
            activityCount: 0,
            negativeMood: 0,
            positiveMood: 0,
            weekOf,
            weekNumber: 0,
            moodCount: 0,
            motivation: 0,
            motivationCount: 0
          };
        }
        newHistory[weekOf].positiveMood += Number(d.response) || 0;
        newHistory[weekOf].moodCount += 1;
      });
      data.negativeMood.forEach((d) => {
        const weekOf = format(startOfWeek(addHours(d.answeredAt, offset), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!newHistory[weekOf]) {
          newHistory[weekOf] = {
            activityCount: 0,
            negativeMood: 0,
            positiveMood: 0,
            weekOf,
            weekNumber: 0,
            moodCount: 0,
            motivation: 0,
            motivationCount: 0
          };
        }
        newHistory[weekOf].negativeMood += Number(d.response) || 0;
      });
      data.motivation.forEach((w) => {
        const weekOf = format(startOfWeek(addHours(w.answeredAt, offset), { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!newHistory[weekOf]) {
          newHistory[weekOf] = {
            activityCount: 0,
            negativeMood: 0,
            positiveMood: 0,
            weekOf,
            weekNumber: 0,
            moodCount: 0,
            motivation: 0,
            motivationCount: 0
          };
        }
        newHistory[weekOf].motivation += Number(w.response) || 0;
        newHistory[weekOf].motivationCount += 1;
      });

      let weeks = Object.values(newHistory);
      weeks.sort((a, b) => new Date(a.weekOf).getTime() - new Date(b.weekOf).getTime());
      weeks = weeks.map((w, index) => ({
        ...w,
        weekNumber: index
      }));
      setHistory(weeks.reverse());
    }
  }, [data]);

  if (loading) {
    return <LoadingBackdrop />;
  }
  return (
    <Stack gap={3}>
      {history.map((h, index) => (
        <WeekDisplay index={index} key={h.weekOf} week={h} prev={history[index + 1]} />
      ))}
    </Stack>
  );
};

export interface WeekDisplayProp {
  week: Weeks;
  prev?: Weeks;
  index: number;
}

export const WeekDisplay: FC<WeekDisplayProp> = ({ week, prev, index }) => {
  const lastWeek = !prev;
  const ac = week.activityCount >= (prev?.activityCount || 0);
  const avgPosMood = week.positiveMood / (week.moodCount || 1);
  const avgNegMood = week.negativeMood / (week.moodCount || 1);
  const prevAvgPosMood = (prev?.positiveMood ?? 0) / (prev?.moodCount || 1);
  const prevAvgNegMood = (prev?.negativeMood ?? 0) / (prev?.moodCount || 1);
  const pMd = avgPosMood >= prevAvgPosMood;
  const nMd = avgNegMood <= prevAvgNegMood;
  const md = avgPosMood - avgNegMood >= prevAvgPosMood - prevAvgNegMood;
  const avgMotivation = week.motivation / (week.motivationCount || 1);
  const mo = avgMotivation >= (prev?.motivation ?? 0) / (prev?.motivationCount || 1);
  const offset = new Date().getTimezoneOffset() / 60;
  return (
    <>
      <Card>
        <CardContent>
          <Stack>
            <Typography variant="h6" gutterBottom>
              Week {week.weekNumber + 1} {index === 0 ? '(Current Week)' : ''}
            </Typography>
            <Stack>
              <Typography variant="subtitle1">
                {format(addHours(new Date(week.weekOf), offset), 'EEE MMM d')} - {format(addDays(new Date(week.weekOf), 7), 'EEE MMM d')}
              </Typography>
            </Stack>
            <Grid spacing={2} container sx={{ my: 2 }}>
              {!!avgPosMood && (
                <Grid item justifyContent="center" textAlign="center" xs={6} md={3}>
                  <Chip
                    sx={{ width: '30vw' }}
                    color={lastWeek ? 'default' : md ? 'primary' : 'error'}
                    icon={!lastWeek ? <FontAwesomeIcon icon={pMd ? faFaceSmile : faFaceFrown} /> : undefined}
                    label={avgPosMood.toFixed(1)}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    Avg Positive Mood
                  </Typography>
                </Grid>
              )}
              <Grid item justifyContent="center" textAlign="center" xs={6} md={3}>
                <Chip
                  sx={{ width: '30vw' }}
                  color={lastWeek ? 'default' : ac ? 'primary' : 'error'}
                  icon={!lastWeek ? <FontAwesomeIcon icon={ac ? faArrowUp : faArrowDown} /> : undefined}
                  label={week.activityCount}
                />
                <Typography variant="subtitle2" gutterBottom>
                  # Activities
                </Typography>
              </Grid>
              {!!avgNegMood && (
                <Grid item justifyContent="center" textAlign="center" xs={6} md={3}>
                  <Chip
                    sx={{ width: '30vw' }}
                    color={lastWeek ? 'default' : md ? 'primary' : 'error'}
                    icon={!lastWeek ? <FontAwesomeIcon icon={nMd ? faFaceSmile : faFaceFrown} /> : undefined}
                    label={avgNegMood.toFixed(1)}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    Avg Negative Mood
                  </Typography>
                </Grid>
              )}
              {!!week.motivation && (
                <Grid item justifyContent="center" textAlign="center" xs={6} md={3}>
                  <Chip
                    sx={{ width: '30vw' }}
                    color={lastWeek ? 'default' : mo ? 'primary' : 'error'}
                    icon={!lastWeek ? <FontAwesomeIcon icon={mo ? faStar : outlineStar} /> : undefined}
                    label={`${avgMotivation.toFixed(0)} / 10`}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    Motivation
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Stack>
        </CardContent>
      </Card>
      {index === 0 && !lastWeek && <Typography>{getMessage(md, ac, mo)}</Typography>}
    </>
  );
};
